import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { FormDialogActions, FormDialogContent, FormFieldStack, InfoBanner } from '../team.styles';

const schema = z.object({
  billableRate: z.preprocess(
    (v) => (v == null || (typeof v === 'number' && isNaN(v)) ? undefined : Number(v)),
    z.number().min(0, 'Must be 0 or greater').optional(),
  ),
});

type FormValues = z.infer<typeof schema>;

interface SetBillableRateModalProps {
  open: boolean;
  memberName: string;
  currentRate?: number;
  onClose: () => void;
  onSave: (rate: number | undefined) => void;
  isPending?: boolean;
}

export const SetBillableRateModal: React.FC<SetBillableRateModalProps> = ({
  open,
  memberName,
  currentRate,
  onClose,
  onSave,
  isPending = false,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { billableRate: currentRate ?? NaN },
  });

  useEffect(() => {
    if (open) reset({ billableRate: currentRate ?? NaN });
  }, [open, currentRate, reset]);

  const handleFormSubmit = (values: FormValues) => {
    onSave(values.billableRate);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Set rate</DialogTitle>

      <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
        <FormDialogContent>
          <FormFieldStack spacing={2}>
            <InfoBanner variant="body2">
              This rate will be applied to all entries made by {memberName}, unless projects have
              their own rate.
            </InfoBanner>

            <TextField
              label="What is the new billable rate"
              type="number"
              autoFocus
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              error={Boolean(errors.billableRate)}
              helperText={errors.billableRate?.message}
              {...register('billableRate', { valueAsNumber: true })}
            />
          </FormFieldStack>
        </FormDialogContent>

        <FormDialogActions>
          <Button onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            disableElevation
            disabled={isPending}
            startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Save
          </Button>
        </FormDialogActions>
      </form>
    </Dialog>
  );
};
